import './scheduleTable_new.css';
import { useMemo, useEffect, useState } from "react";
import TableInput from "../../../components/common/table_input/tableInput";
import { getInputConfigs, getColConfigs } from "./tableConfig";
import TabToggle from "../../../components/common/tab_toggle/tabToggle";
import { useApiData } from "../../../contexts/apiService";
import { getUniqueDropdownOptions } from "../../../helper_functions";
import SQLTable from "../../../components/sql_table/tableMain";
import { useUserAccess, USER_ROLE_LEVEL } from '../../../contexts/userAccess';
import { useDataManager } from "../../../contexts/dataManager";
import { useTicketsContext } from '../../../contexts/pdfTicketsContext';

const ScheduleTable = () => {
    const { apiData } = useApiData();
    const { requestTableData, hasTableData } = useDataManager();
    const { availableTickets } = useTicketsContext();
    const [activeTable, setActiveTable] = useState<string>('schedule_table');
    const { userRole = 'viewer' } = useUserAccess();

    useEffect(() => {
        // Request supporting reference data shared across tabs
        if (['schedule_table', 'vac_schedule_table_vs'].includes(activeTable)) {
            if (!hasTableData('entity_definition'))  requestTableData('entity_definition');
            if (!hasTableData('producer_definition'))  requestTableData('producer_definition');
            if (!hasTableData('lease_coordinates'))    requestTableData('lease_coordinates');
            if (!hasTableData('carrier_definition'))   requestTableData('carrier_definition');
            if (!hasTableData('market_coordinates'))    requestTableData('market_coordinates');
            if (!hasTableData('carrier_rate_context'))  requestTableData('carrier_rate_context');
        }
        if (activeTable === 'schedule_table') {
            if (!hasTableData('crude_tanks_definition')) requestTableData('crude_tanks_definition');
            if (!hasTableData('market_discounts'))      requestTableData('market_discounts');
        }
        if (activeTable === 'vac_schedule_table_vs') {
            if (!hasTableData('vac_unassigned_oi_tickets')) requestTableData('vac_unassigned_oi_tickets');
        }
    }, [activeTable]);

    const producerOptions = getUniqueDropdownOptions({
        tableData:apiData?.producer_definition,
        valueColumn: 'id',
        labelColumn: 'producer_name'
    });

    const entityOptions = getUniqueDropdownOptions({
        tableData:apiData?.entity_definition,
        valueColumn: 'id',
        labelColumn: 'entity_name',
        extraFields: ['current_producer_id']
    });
    
    // Get all lease options with producer information
    const allLeaseOptions = getUniqueDropdownOptions({
        tableData: apiData?.lease_coordinates,
        valueColumn: 'id',
        labelColumn: 'lease',
        extraFields: ['producer_id', 'producer_name', 'product_type', 'region', 'entity_id']
    });

    const ALLOWED_PRODUCTS_BY_TABLE: Record<string, string[] | null> = {
        'schedule_table': ['Skim Oil', 'Skim', 'Lease Crude', 'Sour Crude', 'Condensate'],
        'bottoms_schedule_table': null, // all allowed
        'vac_schedule_table_vs': null, // all allowed
        'ngl_schedule_table': null // all allowed,
    };

    // Filter leases to only include those with active producers and allowed product types
    const leaseOptions = useMemo(() => {
        if (!allLeaseOptions.length || !apiData?.producer_definition) {
            return allLeaseOptions;
        }
        
        // Create a map of producer_id to is_active status for quick lookup
        const producerActiveMap = new Map(
            apiData.producer_definition.map((producer: any) => [producer.id, producer.is_active])
        );

        const allowedProducts = ALLOWED_PRODUCTS_BY_TABLE[activeTable] ?? null;
        
        return allLeaseOptions.filter((lease: any) => {
            const producerIsActive = producerActiveMap.get(lease.producer_id);
            if (producerIsActive !== 1 && producerIsActive !== true) return false;
            if (allowedProducts !== null && !allowedProducts.includes(lease.product_type)) return false;
            return true;
        });
    }, [allLeaseOptions, apiData?.producer_definition, activeTable]);
    
    const destinationOptions = getUniqueDropdownOptions({
        tableData: apiData?.market_coordinates,
        valueColumn: 'id',
        labelColumn: 'station'
    });
    const carrierOptions = getUniqueDropdownOptions({
        tableData: apiData?.carrier_definition,
        valueColumn: 'id',
        labelColumn: 'name',
        extraFields: ['approved_product_types', 'operating_basin'],
    });

    const INPUT_CONFIGS = getInputConfigs(activeTable, producerOptions, entityOptions, leaseOptions, carrierOptions);
    const COL_CONFIGS = getColConfigs(activeTable, producerOptions, entityOptions, leaseOptions, destinationOptions, carrierOptions, apiData?.vac_unassigned_oi_tickets ?? [], userRole, apiData?.carrier_rate_context ?? null);
    
    const SCHEDULER = USER_ROLE_LEVEL['scheduler'];
    const showInputsBase = Number(USER_ROLE_LEVEL[userRole]) == SCHEDULER
        || Number(USER_ROLE_LEVEL[userRole]) == 4
            || Number(USER_ROLE_LEVEL[userRole]) == 6
                || Number(USER_ROLE_LEVEL[userRole]) == 7

    const showInputs = (activeTable === "bottoms_schedule_table" || activeTable === "vac_schedule_table_vs") ?
        Number(USER_ROLE_LEVEL[userRole]) !== USER_ROLE_LEVEL['viewer']
        : showInputsBase;
        
    const showTable =  Number(USER_ROLE_LEVEL[userRole]) >= 1;

    const tabOptions = [
        { label: 'Crude', value: 'schedule_table' },
        { label: 'Bottoms', value: 'bottoms_schedule_table' },
        { label: 'NGLs', value: 'ngl_schedule_table' },
        { label: 'Vac Truck', value: 'vac_schedule_table_vs' },
    ];
    
    const getTableData = useMemo(() => {
        switch (activeTable) {
            case 'schedule_table':
                const scheduleData = apiData?.schedule_table?.map(item => {
                    const identifier = `${item.ticket_number}_${item.carrier_name}`;
                    return { ...item, available_pdf: availableTickets.includes(identifier) ? 1 : 0 };
                }) || [];
                return scheduleData;

            case 'bottoms_schedule_table':
                const bottomsData = apiData?.bottoms_schedule_table || []
                return bottomsData;

            case 'ngl_schedule_table':
                const nglData = apiData?.ngl_schedule_table || []
                return nglData;
            case 'vac_schedule_table_vs':
                return (apiData?.vac_schedule_table_vs || []).map((row: any) => {
                        
                        const oiLineItems = row.oi_line_items
                            ? (typeof row.oi_line_items === 'string'
                                ? (() => { try { return JSON.parse(row.oi_line_items) ?? []; } catch { return []; } })()
                                : row.oi_line_items)
                            : [];
                        const allStatuses: string[] = oiLineItems
                            .map((li: any) => li.oi_status)
                            .filter((s: any) => s != null && s !== '');
                        const oiStatuses = Array.from(new Set(allStatuses));
                        const oi_status =
                            oiStatuses.length === 0 ? null :
                            oiStatuses.length === 1 ? oiStatuses[0] :
                            'Partial';
                        const allLocations: string[] = oiLineItems
                            .map((li: any) => li.oi_disposal_location)
                            .filter((l: any) => l != null && l !== '');
                        const uniqueLocations = Array.from(new Set(allLocations));
                        const oi_disposal_location =
                            uniqueLocations.length === 0 ? null :
                            uniqueLocations.length === 1 ? uniqueLocations[0] :
                            'Varied';
                        const drivers = oiLineItems
                                .map((li: any) => li.oi_driver)
                                .filter((d: any) => d != null && d !== '');
                        const uniqueDrivers = Array.from(new Set(drivers));
                        const driver =
                            uniqueDrivers.length === 0 ? null :
                            uniqueDrivers.length === 1 ? uniqueDrivers[0] :
                            'Varied';
                        const oi_total_hours = oiLineItems.reduce((s: number, li: any) => s + (li.oi_hours ?? 0), 0);
                        const oi_total_disposal_vol = oiLineItems
                            .filter((li: any) => li.ticket_type === 'disposal')
                            .reduce((s: number, li: any) => s + (li.oi_volume ?? 0), 0);
                        const oi_total_freshwater_vol = oiLineItems
                            .filter((li: any) => li.ticket_type === 'freshwater')
                            .reduce((s: number, li: any) => s + (li.oi_volume ?? 0), 0);
                        return {
                            ...row,
                            oi_line_items:           oiLineItems,
                            oi_status,
                            oi_disposal_location,
                            driver,
                            oi_total_hours:          oi_total_hours || null,
                            oi_total_disposal_vol:   oi_total_disposal_vol || null,
                            oi_total_freshwater_vol: oi_total_freshwater_vol || null,
                        };
                    });
                
            default:
                return [];
        }
    }, [activeTable, apiData, availableTickets]);

    return (
        <div className="scheduleTable-mainContainer">
            <div className="scheduleTable-tabContainer">
                <TabToggle
                    tabOptions={tabOptions}
                    activeTab={activeTable}
                    onChange={setActiveTable}
                />
            </div>

            {showInputs && (
                <div className="scheduleTable-inputContainer">
                    <TableInput
                        tableName={activeTable}
                        inputColumnConfigs={INPUT_CONFIGS[activeTable].columns}
                        addMultipleRows={true}
                    />
                </div>
            )}

            <div className={`scheduleTable-tableContainer${['bottoms_schedule_table', 'vac_schedule_table_vs'].includes(activeTable) ? ' scheduleTable-tableContainer--bottoms' : ''}`}>
                <SQLTable
                    key={activeTable}
                    tableName={activeTable}
                    tableData={getTableData}
                    idColumn={'id'}
                    columnConfigs={COL_CONFIGS[activeTable].columns}
                    showHeader={true}
                    selectableRows={activeTable !== null}
                    multipleSelection={false}
                    deletableRows={activeTable !== null}
                    deletionRequiresApproval={true}
                    enableFilters={true}
                    persistFilters={true}
                    enableWarnings={true}
                    enableSettings={COL_CONFIGS[activeTable]?.enableSettings}
                    enableColumnConfiguration={true}
                    warningRules={COL_CONFIGS[activeTable].warningRules}
                    sortOrder={COL_CONFIGS[activeTable].sortOrder}
                    rowRibbon={COL_CONFIGS[activeTable].rowRibbon}
                    rowColor={COL_CONFIGS[activeTable].rowColor}
                    exportColumnOrder={COL_CONFIGS[activeTable].exportColumnOrder}
                    userRoleLevel={USER_ROLE_LEVEL[userRole] || 1}
                    enableSummary={true}
                    summaryConfig={COL_CONFIGS[activeTable].tableSummary}
                    monthNavigation={true}
                    showTable={showTable}
                />
            </div>
        </div>
    );
};

export default ScheduleTable;